/**
 * Offline Queue Manager — v2 (post root-cause investigation)
 *
 * Key changes vs v1:
 *  • Error classification: network / 5xx → retry forever w/ backoff; 401 → re-auth; 4xx → mark failed
 *  • Pending count NO LONGER includes failed items (banner truth-in-advertising)
 *  • Per-item processing is fully isolated — one bad payload can NEVER block others
 *  • Stale "failed" items don't auto-reset on every sync (was masking real errors)
 *  • Public helpers exposed for the Sync Queue Recovery page
 */

import { put, getAll, remove, STORES } from './offlineDB';
import api, { apiHelpers } from './api';
import { logSync } from './syncLogger';

// Action types that can be queued offline
export const OFFLINE_ACTIONS = {
  CREATE_CLIENT: 'CREATE_CLIENT',
  UPDATE_CLIENT: 'UPDATE_CLIENT',
  CREATE_ESTIMATE: 'CREATE_ESTIMATE',
  UPDATE_ESTIMATE: 'UPDATE_ESTIMATE',
  UPDATE_JOB_STATUS: 'UPDATE_JOB_STATUS',
  ADD_JOB_NOTES: 'ADD_JOB_NOTES',
  ADD_JOB_PHOTOS: 'ADD_JOB_PHOTOS',
  ADD_ESTIMATE_NOTES: 'ADD_ESTIMATE_NOTES',
  UPDATE_SITE_VISIT_FIELD_NOTES: 'UPDATE_SITE_VISIT_FIELD_NOTES',
};

// Timeout for individual sync API calls
const SYNC_CALL_TIMEOUT_MS = 30000;     // 30s per item (photos need room)
const SYNC_BATCH_TIMEOUT_MS = 5 * 60_000; // 5 min for entire batch
// Exponential backoff bases (ms). CAPPED AT 60S so a flaky hotspot doesn't
// trap users in 15-minute silent stretches (the SolidHome incident: users
// saw "syncing 1 pending change…" for 30+ minutes with no visible progress
// because retries 5+ all waited 15 minutes between attempts). The lib still
// retries forever; it just retries more frequently.
const BACKOFF_LADDER_MS = [1_000, 4_000, 10_000, 30_000, 60_000];

/* ============================================================
 *  Queue write path
 * ============================================================ */

/**
 * Add an action to the offline queue, deduplicating updates to the same resource.
 * If an item for the same resource exists in "failed" state, we REPLACE it (latest wins).
 */
export async function queueOfflineAction(actionType, payload, orgId) {
  const action = {
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    action_type: actionType,
    payload,
    org_id: orgId,
    created_at: new Date().toISOString(),
    status: 'pending',
    retries: 0,
    next_attempt_at: new Date().toISOString(),
    last_error: null,
    last_error_class: null,
  };

  // Deduplication — replace any pending/failed action for the SAME resource
  const all = await getAll(STORES.OFFLINE_QUEUE);
  const isSame = (a) => {
    if (a.action_type !== actionType) return false;
    if (actionType === OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES) {
      return a.payload?.site_visit_id === payload?.site_visit_id;
    }
    if ([OFFLINE_ACTIONS.UPDATE_CLIENT, OFFLINE_ACTIONS.UPDATE_ESTIMATE, OFFLINE_ACTIONS.ADD_JOB_NOTES,
         OFFLINE_ACTIONS.UPDATE_JOB_STATUS, OFFLINE_ACTIONS.ADD_JOB_PHOTOS, OFFLINE_ACTIONS.ADD_ESTIMATE_NOTES].includes(actionType)) {
      return a.payload?.id === payload?.id;
    }
    return false;
  };
  for (const a of all.filter(isSame)) {
    await remove(STORES.OFFLINE_QUEUE, a.id);
  }

  await put(STORES.OFFLINE_QUEUE, action);
  return action;
}

/* ============================================================
 *  Queue read helpers
 * ============================================================ */

export async function getAllActions() {
  return getAll(STORES.OFFLINE_QUEUE);
}

export async function getPendingActions() {
  const actions = await getAll(STORES.OFFLINE_QUEUE);
  return actions
    .filter(a => a.status === 'pending')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export async function getFailedActions() {
  const actions = await getAll(STORES.OFFLINE_QUEUE);
  return actions
    .filter(a => a.status === 'failed')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

/** Pending count — ONLY pending, NOT failed. (Bug-2 fix) */
export async function getPendingCount() {
  return (await getPendingActions()).length;
}

export async function getFailedCount() {
  return (await getFailedActions()).length;
}

/** Reset specific failed items back to pending (used by recovery UI Retry button). */
export async function retryFailedAction(actionId) {
  const all = await getAll(STORES.OFFLINE_QUEUE);
  const action = all.find(a => a.id === actionId);
  if (!action) return false;
  action.status = 'pending';
  action.retries = 0;
  action.last_error = null;
  action.last_error_class = null;
  action.next_attempt_at = new Date().toISOString();
  await put(STORES.OFFLINE_QUEUE, action);
  return true;
}

/** Reset ALL failed items back to pending (legacy "Sync Now" behavior). */
export async function resetFailedActions() {
  const failed = await getFailedActions();
  for (const a of failed) {
    a.status = 'pending';
    a.retries = 0;
    a.last_error = null;
    a.last_error_class = null;
    a.next_attempt_at = new Date().toISOString();
    await put(STORES.OFFLINE_QUEUE, a);
  }
  return failed.length;
}

export async function discardAction(actionId) {
  await remove(STORES.OFFLINE_QUEUE, actionId);
}

export async function clearFailedActions() {
  const failed = await getFailedActions();
  for (const a of failed) await remove(STORES.OFFLINE_QUEUE, a.id);
  return failed.length;
}

export async function clearAllActions() {
  const all = await getAll(STORES.OFFLINE_QUEUE);
  for (const a of all) await remove(STORES.OFFLINE_QUEUE, a.id);
  return all.length;
}

/* ============================================================
 *  Error classification
 * ============================================================ */

/**
 * Classify an error so the sync loop knows what to do.
 * Returns one of: 'auth' | 'validation' | 'network' | 'server' | 'unknown'
 *  - auth:        401/403 → mark as failed (needs re-auth)
 *  - validation:  4xx (other) → mark as failed (payload is bad, no point retrying)
 *  - network:     no response, timeout, offline → retry forever w/ backoff
 *  - server:      5xx → retry forever w/ backoff
 *  - unknown:     anything else → treat like network (retry forever)
 */
function classifyError(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return 'auth';
  if (status >= 400 && status < 500) return 'validation';
  if (status >= 500 && status < 600) return 'server';
  if (err?.code === 'ECONNABORTED' || err?.message?.includes('timed out')) return 'network';
  if (!err?.response) return 'network'; // axios: no response = network failure
  return 'unknown';
}

function shouldGiveUp(errorClass) {
  return errorClass === 'auth' || errorClass === 'validation';
}

function nextBackoffMs(retries) {
  return BACKOFF_LADDER_MS[Math.min(retries, BACKOFF_LADDER_MS.length - 1)];
}

/* ============================================================
 *  Individual action processor (with timeout)
 * ============================================================ */

function withTimeout(promise, ms, label = 'API call') {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

async function processAction(action) {
  const { action_type, payload, org_id } = action;

  switch (action_type) {
    case OFFLINE_ACTIONS.CREATE_CLIENT:
      return withTimeout(apiHelpers.createClient(org_id, payload), SYNC_CALL_TIMEOUT_MS, 'createClient');

    case OFFLINE_ACTIONS.UPDATE_CLIENT:
      return withTimeout(apiHelpers.updateClient(org_id, payload.id, payload.data), SYNC_CALL_TIMEOUT_MS, 'updateClient');

    case OFFLINE_ACTIONS.CREATE_ESTIMATE:
      return withTimeout(apiHelpers.createEstimate(org_id, payload), SYNC_CALL_TIMEOUT_MS, 'createEstimate');

    case OFFLINE_ACTIONS.UPDATE_ESTIMATE:
      return withTimeout(apiHelpers.updateEstimate(org_id, payload.id, payload.data), SYNC_CALL_TIMEOUT_MS, 'updateEstimate');

    case OFFLINE_ACTIONS.UPDATE_JOB_STATUS:
      return withTimeout(apiHelpers.updateJob(org_id, payload.id, { status: payload.status }), SYNC_CALL_TIMEOUT_MS, 'updateJobStatus');

    case OFFLINE_ACTIONS.ADD_JOB_NOTES:
      return withTimeout(apiHelpers.updateJob(org_id, payload.id, { internal_notes: payload.notes }), SYNC_CALL_TIMEOUT_MS, 'addJobNotes');

    case OFFLINE_ACTIONS.ADD_JOB_PHOTOS:
      return withTimeout(apiHelpers.updateJob(org_id, payload.id, { photos: payload.photos }), SYNC_CALL_TIMEOUT_MS, 'addJobPhotos');

    case OFFLINE_ACTIONS.ADD_ESTIMATE_NOTES:
      return withTimeout(apiHelpers.updateEstimate(org_id, payload.id, { internal_notes: payload.notes }), SYNC_CALL_TIMEOUT_MS, 'addEstimateNotes');

    case OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES: {
      // Two-phase: upload base64 photos individually, then sync text.
      // A failed photo upload does NOT block the text save — it's preserved in the payload.
      let fieldNotes = { ...payload.field_notes };

      if (fieldNotes.photos?.length) {
        const resolvedPhotos = [];
        for (const photo of fieldNotes.photos) {
          if (photo.dataUrl && photo.dataUrl.startsWith('data:')) {
            try {
              const [header, b64] = photo.dataUrl.split(',');
              const mime = header.match(/:(.*?);/)[1];
              const bytes = atob(b64);
              const arr = new Uint8Array(bytes.length);
              for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
              const blob = new Blob([arr], { type: mime });

              const formData = new FormData();
              formData.append('file', blob, photo.name || 'photo.jpg');
              formData.append('name', photo.name || 'photo.jpg');
              formData.append('tag', photo.tag || '');

              const res = await withTimeout(
                api.post(
                  `/organizations/${org_id}/site-visits/${payload.site_visit_id}/upload-field-photo`,
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
                ),
                SYNC_CALL_TIMEOUT_MS,
                'uploadFieldPhoto'
              );
              resolvedPhotos.push({
                id: res.data.id, url: res.data.url, dataUrl: res.data.url,
                name: res.data.name || photo.name, tag: res.data.tag || photo.tag,
                timestamp: res.data.timestamp || photo.timestamp,
              });
            } catch (photoErr) {
              // Keep the local base64 — text save still proceeds, photo retries next round
              console.warn('[OfflineQueue] Photo upload failed (will retry):', photoErr.message);
              resolvedPhotos.push(photo);
            }
          } else {
            resolvedPhotos.push(photo);
          }
        }
        fieldNotes.photos = resolvedPhotos;
      }

      return withTimeout(
        apiHelpers.updateSiteVisit(org_id, payload.site_visit_id, fieldNotes, payload.user_id, payload.user_name),
        SYNC_CALL_TIMEOUT_MS,
        'updateFieldNotes'
      );
    }

    default:
      // Unknown action — mark as validation error (won't retry forever)
      throw Object.assign(new Error(`Unknown action type: ${action_type}`), {
        response: { status: 400 },
      });
  }
}

/* ============================================================
 *  Batch sync
 * ============================================================ */

/**
 * Sync all pending offline actions.
 *
 * `force=true` skips the backoff check — used when the USER explicitly tapped
 * "Sync now" / "Retry now". Without this the Sync button is a no-op for items
 * already pushed out to a 15-min backoff (the SolidHome stuck-queue bug,
 * Feb 2026).
 *
 * Returns { success, failed, deferred, total, timedOut, authError }
 */
export async function syncOfflineActions(onProgress, { force = false } = {}) {
  // When forced, knock every pending item out of its backoff window so this
  // batch actually attempts the network. We DO NOT touch the retries counter
  // — that history is useful for diagnostics.
  if (force) {
    const all = await getAll(STORES.OFFLINE_QUEUE);
    const nowIso = new Date().toISOString();
    for (const a of all) {
      if (a.status === 'pending' && a.next_attempt_at && new Date(a.next_attempt_at).getTime() > Date.now()) {
        a.next_attempt_at = nowIso;
        await put(STORES.OFFLINE_QUEUE, a);
      }
    }
    logSync('info', 'sync.force_retry_armed', {});
  }

  const pendingActions = await getPendingActions();
  if (pendingActions.length === 0) {
    return { success: 0, failed: 0, deferred: 0, total: 0, timedOut: false, authError: false };
  }

  console.log('[OfflineQueue v2] Syncing', pendingActions.length, 'actions');
  let success = 0, failed = 0, deferred = 0, timedOut = false, authError = false;
  const batchStart = Date.now();
  const now = Date.now();

  for (let i = 0; i < pendingActions.length; i++) {
    if (Date.now() - batchStart > SYNC_BATCH_TIMEOUT_MS) {
      logSync('warn', 'sync.batch_timeout', { processed: i, total: pendingActions.length });
      timedOut = true;
      break;
    }

    const action = pendingActions[i];

    // Yield to the main thread between items so the UI thread can paint and
    // touches stay responsive on iPad Safari. This is part of the
    // "soft-lock during sync" fix (Feb 2026, SolidHome).
    await new Promise(r => setTimeout(r, 0));

    // Respect backoff — skip items that aren't due yet
    if (action.next_attempt_at && new Date(action.next_attempt_at).getTime() > now) {
      deferred++;
      onProgress?.({ current: i + 1, total: pendingActions.length, success, failed, deferred });
      continue;
    }

    try {
      await processAction(action);
      // Success → remove from queue
      await remove(STORES.OFFLINE_QUEUE, action.id);
      success++;
      logSync('info', 'sync.item_success', { action_type: action.action_type, retries: action.retries });
    } catch (error) {
      const cls = classifyError(error);
      action.retries = (action.retries || 0) + 1;
      action.last_error = (error.message || 'Unknown error').slice(0, 500);
      action.last_error_class = cls;

      if (shouldGiveUp(cls)) {
        action.status = 'failed';
        if (cls === 'auth') authError = true;
        failed++;
        logSync('error', 'sync.item_failed', {
          action_type: action.action_type, error_class: cls, error: action.last_error,
        });
      } else {
        // Stay pending, schedule next attempt with backoff
        const wait = nextBackoffMs(action.retries - 1);
        action.next_attempt_at = new Date(now + wait).toISOString();
        deferred++;
        logSync('warn', 'sync.item_deferred', {
          action_type: action.action_type, error_class: cls, retry: action.retries, wait_ms: wait,
        });
      }
      await put(STORES.OFFLINE_QUEUE, action);
    }

    onProgress?.({ current: i + 1, total: pendingActions.length, success, failed, deferred });
  }

  logSync('info', 'sync.batch_done', { success, failed, deferred, total: pendingActions.length, timed_out: timedOut, auth_error: authError });
  return { success, failed, deferred, total: pendingActions.length, timedOut, authError };
}

/* ============================================================
 *  Diagnostics (for the recovery page)
 * ============================================================ */

/** Dump everything in IndexedDB for the recovery page. */
export async function dumpQueueDiagnostics() {
  const { getAllPhotoUploads } = await import('./photoUploadManager');
  const { getRecentLogs } = await import('./syncLogger');
  const actions = await getAll(STORES.OFFLINE_QUEUE);
  const drafts = await getAll(STORES.FIELD_NOTES_DRAFTS);
  const photos = await getAllPhotoUploads();
  const logs = await getRecentLogs(200);
  return {
    generated_at: new Date().toISOString(),
    user_agent: navigator.userAgent,
    online: navigator.onLine,
    pending_count: actions.filter(a => a.status === 'pending').length,
    failed_count: actions.filter(a => a.status === 'failed').length,
    pending_photos: photos.filter(p => p.status === 'pending').length,
    failed_photos: photos.filter(p => p.status === 'failed').length,
    actions: actions.map((a) => ({
      id: a.id,
      action_type: a.action_type,
      status: a.status,
      retries: a.retries,
      created_at: a.created_at,
      next_attempt_at: a.next_attempt_at,
      last_error: a.last_error,
      last_error_class: a.last_error_class,
      // Strip giant photo blobs from preview — keep their count
      payload_preview: previewPayload(a.payload),
    })),
    photo_uploads: photos.map((p) => ({
      id: p.id,
      site_visit_id: p.site_visit_id,
      name: p.name, tag: p.tag,
      status: p.status, retries: p.retries,
      size_kb: Math.round((p.data_url?.length || 0) / 1024),
      created_at: p.created_at,
      next_attempt_at: p.next_attempt_at,
      last_error: p.last_error,
      last_error_class: p.last_error_class,
    })),
    drafts: drafts.map((d) => ({
      id: d.id,
      site_visit_id: d.site_visit_id,
      org_id: d.org_id,
      updated_at: d.updated_at,
      synced: d.synced,
      preview: previewPayload(d.notes || d),
    })),
    recent_logs: logs,
  };
}

function previewPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (Array.isArray(v) && k.toLowerCase().includes('photo')) {
      out[k] = `[${v.length} photo(s)]`;
    } else if (typeof v === 'string' && v.startsWith('data:')) {
      out[k] = '[base64 blob]';
    } else if (Array.isArray(v) && v.length > 20) {
      out[k] = `[array of ${v.length}]`;
    } else if (typeof v === 'object' && v !== null) {
      out[k] = previewPayload(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}
